import moment from 'moment';

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Unwrap axios `response.data` envelopes from dashboard APIs. */
export function unwrapApiList(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

/** Human-readable x-axis labels for summary + legacy chart periods. */
export function formatDashboardChartPeriodLabel(period) {
  const raw = String(period ?? '').trim();
  if (!raw) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return moment(raw, 'YYYY-MM-DD', true).format('D MMM');
  }
  if (/^\d{4}-\d{2}$/.test(raw)) {
    return moment(raw, 'YYYY-MM', true).format('MMM YYYY');
  }
  if (/^\d{4}$/.test(raw)) {
    return raw;
  }

  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format('D MMM') : raw;
}

function resolveUserRef(item) {
  if (item?.user && typeof item.user === 'object') return item.user;
  if (item?.userId && typeof item.userId === 'object') return item.userId;
  return null;
}

/**
 * Maps `lists.topCustomersLtv` from GET /dashboard/admin/summary.
 * Display: title = displayName | email | firstname + lastname; subtitle = phone.
 */
export function normalizeTopCustomerItem(item) {
  if (!item || typeof item !== 'object') {
    return {
      userId: '',
      displayName: 'Customer',
      phone: '',
      email: '',
      img: null,
      deliveredOrders: 0,
      lifetimeValue: 0,
    };
  }

  const user = resolveUserRef(item);
  const userId =
    (typeof item.userId === 'string' ? item.userId : null) ??
    user?._id ??
    user?.id ??
    item?.id ??
    item?._id ??
    '';

  const firstname = item?.firstname ?? user?.firstname ?? '';
  const lastname = item?.lastname ?? user?.lastname ?? '';
  const email = String(item?.email ?? user?.email ?? '').trim();
  const phone = String(item?.phone ?? user?.phone ?? '').trim();
  const apiDisplayName = String(item?.displayName ?? '').trim();
  const nameFromParts = `${firstname} ${lastname}`.trim();
  const displayName = apiDisplayName || email || nameFromParts || 'Customer';

  return {
    userId: userId != null ? String(userId) : '',
    displayName,
    phone,
    email,
    img: item?.img ?? user?.img ?? null,
    deliveredOrders: toNumber(
      item?.deliveredOrders ?? item?.delivered_orders ?? item?.count,
    ),
    lifetimeValue: toNumber(
      item?.lifetimeValue ??
        item?.lifetime_value ??
        item?.totalPrice ??
        item?.total_price,
    ),
  };
}

export function normalizeTopCustomersList(list) {
  return (Array.isArray(list) ? list : [])
    .map(normalizeTopCustomerItem)
    .sort((a, b) => b.lifetimeValue - a.lifetimeValue);
}

function resolveProductRef(item) {
  if (item?.product && typeof item.product === 'object') return item.product;
  if (item?.productId && typeof item.productId === 'object') return item.productId;
  return null;
}

/** Maps `lists.topSoldProducts` from summary (or legacy statistics/products shape). */
export function normalizeTopProductItem(item) {
  if (!item || typeof item !== 'object') {
    return {
      productTitle: 'Product',
      productImg: null,
      unitsSold: 0,
      revenue: 0,
    };
  }

  const product = resolveProductRef(item);
  const translation = product?.translation ?? item?.translation ?? {};
  const productTitle =
    item?.productTitle ??
    item?.product_title ??
    item?.title ??
    translation?.title ??
    product?.title ??
    product?.name ??
    'Product';

  const productImg =
    item?.productImg ??
    item?.product_img ??
    item?.img ??
    product?.img ??
    product?.image ??
    null;

  return {
    productTitle,
    productImg,
    unitsSold: toNumber(
      item?.unitsSold ??
        item?.units_sold ??
        item?.totalQty ??
        item?.total_qty ??
        item?.count ??
        item?.quantity ??
        item?.sales,
    ),
    revenue: toNumber(
      item?.revenue ??
        item?.total_price ??
        item?.totalPrice ??
        item?.lineRevenue ??
        item?.line_revenue,
    ),
  };
}

export function normalizeTopProductsList(list) {
  return (Array.isArray(list) ? list : [])
    .map(normalizeTopProductItem)
    .sort((a, b) => b.unitsSold - a.unitsSold || b.revenue - a.revenue);
}

/** Maps `charts.salesArea` from summary (or legacy statistics/orders/chart). */
export function normalizeSalesChartPoint(item) {
  if (!item || typeof item !== 'object') {
    return { period: '', value: 0 };
  }

  return {
    period:
      item?.period ??
      item?.time ??
      item?.date ??
      item?.label ??
      item?.day ??
      '',
    value: toNumber(
      item?.value ??
        item?.totalRevenue ??
        item?.total_revenue ??
        item?.total_price ??
        item?.totalPrice ??
        item?.amount ??
        item?.revenue ??
        item?.sum,
    ),
  };
}

export function normalizeSalesChartList(list) {
  return (Array.isArray(list) ? list : []).map(normalizeSalesChartPoint);
}

/** Legacy statistics KPI object → delivery/tax/commission tiles only. */
export function mapLegacyStatisticsEarnings(statistics = {}) {
  return {
    deliveryEarned: toNumber(
      statistics?.deliveryEarned ?? statistics?.delivery_earned,
    ),
    taxEarned: toNumber(statistics?.taxEarned ?? statistics?.tax_earned),
    commissionEarned: toNumber(
      statistics?.commissionEarned ?? statistics?.commission_earned,
    ),
  };
}
